/**
 * SEARCH — ONE BOX OVER EVERY RECORD SCOUT KEEPS, AND A LINK TO EACH ONE.
 *
 * WHAT THIS SCREEN IS FOR. The Owner knows a thing was written down and cannot
 * remember which of ten screens holds it. So this is a LOCATOR, not a reader:
 * every result carries the record's kind, its canonical id, its own timestamp
 * and an excerpt cut from its own text, and every result links to the record in
 * the view that already owns it. Nothing is rendered here that the record does
 * not say, and nothing here is a second copy of a record — a copy is what goes
 * stale.
 *
 * THE QUERY LIVES IN THE PATH, SO A RELOAD KEEPS IT. This workspace has no
 * query-string state anywhere (`paths.ts` parses exactly three path segments),
 * so `/history/search/<query>` is how a search is addressed: it survives a
 * reload, it can be bookmarked, and two searches are two addresses. The kind
 * FILTER is a reading choice rather than a second question, so it is this
 * screen's own state; a filter press narrows what is rendered and deliberately
 * does not move the address. An old `/search/<group>/<query>` link still opens
 * the same query, with the group treated as the reading choice it became.
 * Paging is not in the path either — it is a reading position, not a question,
 * and every other list in this workspace keeps it in memory too.
 *
 * THE COVERAGE LINE IS PART OF THE RESULT, NOT A FOOTNOTE. The gateway reports
 * how far back each record family was actually walked, and this view renders
 * that sentence verbatim rather than paraphrasing it. A short list from a
 * bounded read and a short list from an exhaustive one are different facts, and
 * a screen that showed "3 results" for both would be teaching the Owner that a
 * record does not exist.
 *
 * WHAT IT WILL NOT DO. It does not start research, it does not call a model,
 * and it does not offer "Discuss with Scout" on a record the reference contract
 * cannot carry — an invented reference resolves to nothing, and the Owner would
 * be told their record is malformed. Where a record has no reference kind, the
 * row says which link to use instead and why.
 *
 * EVERY WORD IS REQUIRED, AND THE MATCH IS A SUBSTRING. "retriev evalu" finds a
 * record carrying both fragments in any order; "retrieval" finds "retrievals".
 * The rule is stated on the screen, because a matching rule the reader has to
 * infer is a rule they will get wrong and blame the store for.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import type { GatewayError } from '../gateway';
import { readGateway } from '../gateway';
import { navigate, route } from '../router';
import { createLoadable, expectsMembers, type Resource } from '../resource';
import { DiscussLink } from './discuss';
import { Failure } from './failure';
import { PageHeader } from './about';

const PAGE_LIMIT = 20;

/** The filter, as the path spells it. `all` is the absence of a section, so a
 *  search of everything is the short address and a filtered one says which. */
const GROUPS = [
	{ id: 'everything', label: 'Everything' },
	{ id: 'conversation', label: 'Conversation' },
	{ id: 'research', label: 'Research' },
	{ id: 'science', label: 'Science' },
	{ id: 'governance', label: 'Governance' },
] as const;

type GroupId = (typeof GROUPS)[number]['id'];

interface SearchResult {
	kind: string;
	label: string;
	id: string;
	at: string;
	state: string | null;
	excerpt: string;
	anchor: {
		kind: string;
		id: string;
		conversationId?: string;
		inMainChat?: boolean;
	};
	ref: string | null;
}

interface SearchPayload {
	results: SearchResult[];
	coverage: {
		shown: number;
		hasMore: boolean;
		nextBefore: string | null;
		truncated: boolean;
		familiesSearched: string[];
		windowsTruncated: string[];
		exhaustive: boolean;
		windowRows: number;
		statement: string;
	};
	workspace: { mainChatResolved: boolean; mainChatId: string | null };
	limits: {
		queryMaxChars: number;
		termMinChars: number;
		maxTerms: number;
		pageMax: number;
		match: string;
	};
	notSearched: Array<{ what: string; why: string }>;
}

/**
 * WHERE A RESULT OPENS — one table, and every entry names a place that can
 * actually show the record.
 *
 * THE ROUTES ARE THE CLIENT'S BUSINESS, which is why the gateway sends an
 * `anchor` of `(kind, id)` and not a URL: a server that knew the workspace's
 * addresses would have to change whenever a screen moved, and there would be
 * two places the workspace's shape is written down. This is that one place.
 *
 * `exact` IS THE HONEST HALF. Some records have a surface that opens THEM —
 * a window, an investigation, an experiment, a conversation. Others are read
 * inside a list that the owning screen bounds, so the link lands on the list
 * that holds them and the label says so rather than implying an exact open. The
 * Owner can then see the record's own words in the excerpt above and its id,
 * which is what makes a bounded list navigable by hand.
 */
interface AnchorRoute {
	destination: string;
	section: string | null;
	/** Shown on the link: which screen this opens. */
	label: string;
	/** True when the surface opens THIS record; false when it opens the list
	 *  that holds it. */
	exact: boolean;
}

function anchorRoute(result: SearchResult): AnchorRoute {
	const { anchor } = result;
	switch (anchor.kind) {
		case 'chat_message':
			// THE ONE CONVERSATION IS THE CHAT. A turn in the Main Chat opens the
			// chat; a turn in an archived conversation opens that archive. When
			// the workspace has not been told which conversation is canonical,
			// `inMainChat` is false for everything and the archive is the honest
			// destination — the screen says so below rather than guessing.
			return anchor.inMainChat === true
				? { destination: 'chat', section: null, label: 'Open the Main Chat', exact: false }
				: {
						destination: 'history',
						section: 'conversations',
						label: 'Open the conversation',
						exact: true,
					};
		case 'research_run':
			return { destination: 'research', section: 'window', label: 'Open the window', exact: true };
		case 'research_episode':
			return {
				destination: 'research',
				section: 'observatory',
				label: 'Open the investigation',
				exact: true,
			};
		case 'experiment':
			return {
				destination: 'research',
				section: 'experiments',
				label: 'Open the experiment',
				exact: true,
			};
		case 'space':
			return {
				destination: 'research',
				section: 'spaces',
				label: 'Open the Research Space',
				exact: true,
			};
		case 'hypothesis':
		case 'lesson':
			// THE EVIDENCE VIEW RESOLVES A REFERENCE AGAINST CANONICAL STATE, which
			// is the workspace's record reader: it answers the record's status, its
			// summary and its document, and it says when a record has moved. That
			// is a better answer than a themed screen that merely lists the kind.
			return { destination: 'discoveries', section: 'evidence', label: 'Open the record', exact: true };
		case 'adjudication':
			return {
				destination: 'discoveries',
				section: 'evidence',
				label: 'Open the adjudication',
				exact: false,
			};
		case 'improvement':
			return {
				destination: 'workspace',
				section: 'improvements',
				label: 'Open the proposal',
				exact: false,
			};
		case 'critique':
			return {
				destination: 'workspace',
				section: 'improvements',
				label: 'Open the criticism',
				exact: false,
			};
		case 'charter_decision':
			return {
				destination: 'workspace',
				section: 'decisions',
				label: 'Open the decision',
				exact: false,
			};
		case 'charter_version':
			return {
				destination: 'workspace',
				section: 'charter',
				label: 'Open the charter',
				exact: false,
			};
		default:
			// AN ANCHOR THIS BUILD DOES NOT KNOW IS NAMED RATHER THAN LINKED. A
			// link built from a guess is a link to the wrong record.
			return { destination: 'history', section: 'search', label: '', exact: false };
	}
}

export function SearchView() {
	const current = route.value;
	// THE QUERY IS THE ADDRESS; THE FILTER IS A READING CHOICE. The five-area
	// grammar has three segments, so a filter and a query would need a fourth.
	// The query survives a reload (it is the record id), and the filter is this
	// screen's own state — a filter is a way of reading the answer, not a
	// different question.
	const [group, setGroup] = useState<GroupId>('everything');
	const query = current.recordId;

	const [draft, setDraft] = useState(query ?? '');

	// THE BOX FOLLOWS THE ADDRESS, AND THE ADDRESS IS THE TRUTH. A deep link, a
	// back button or a filter press all change `recordId`, and the input has to
	// show what is actually being searched rather than what was last typed.
	//
	// THE SYNC HAPPENS DURING RENDER, NOT IN AN EFFECT, AND THAT IS A MEASURED
	// CORRECTION RATHER THAN A STYLE. An effect flushes LATER than the render it
	// belongs to, so a mount-time `setDraft('')` could land AFTER the Owner had
	// already typed — wiping the query out of the box while the address still
	// said the screen was searching for it. The specimen that types into the box
	// and submits caught it: the field read back empty and the search never
	// happened. Adjusting the state during render is synchronous with the change
	// that caused it, so a keystroke can never be overtaken by it.
	const [syncedQuery, setSyncedQuery] = useState(query);
	if (query !== syncedQuery) {
		setSyncedQuery(query);
		setDraft(query ?? '');
	}

	const params = useMemo(() => {
		if (query === null || query.trim().length === 0) return null;
		const search = new URLSearchParams({ q: query, limit: String(PAGE_LIMIT) });
		// `everything` IS THE ABSENCE OF A FILTER, so it is not sent: the
		// gateway's own default is every family, and a parameter that repeated it
		// would be a second place the default is written down.
		if (group !== 'everything') search.set('group', group);
		return search.toString();
	}, [query, group]);

	const results = useMemo(
		() =>
			createLoadable<SearchPayload>(
				() =>
					params === null
						? Promise.resolve({ ok: true as const, data: null as never })
						: readGateway<SearchPayload>(`/api/scout/gateway/search?${params}`),
				expectsMembers('results', 'coverage'),
			),
		[params],
	);

	useEffect(() => {
		if (params !== null) void results.load();
	}, [results, params]);

	// PAGING IS APPENDED IN MEMORY, exactly as the ledger's older-calls control
	// works: a page is a reading position, and losing it on a reload costs a
	// scroll rather than a question.
	const [older, setOlder] = useState<SearchResult[]>([]);
	const [paging, setPaging] = useState(false);
	const [pagingError, setPagingError] = useState<GatewayError | null>(null);
	useEffect(() => {
		setOlder([]);
		setPagingError(null);
	}, [params]);

	const state: Resource<SearchPayload> = results.state.value;
	const data = state.data;
	const shown = [...(data?.results ?? []), ...older];

	const submit = (value: string): void => {
		const text = value.trim();
		navigate({
			destination: 'history',
			section: 'search',
			// The path encoder handles the spaces; an empty box is the search
			// screen with no question rather than a search for nothing.
			recordId: text.length === 0 ? null : text,
		});
	};

	const loadOlder = async (): Promise<void> => {
		const cursor = data?.coverage.nextBefore ?? null;
		if (cursor === null || params === null) return;
		setPaging(true);
		setPagingError(null);
		const result = await readGateway<SearchPayload>(
			`/api/scout/gateway/search?${params}&before=${encodeURIComponent(cursor)}`,
		);
		setPaging(false);
		if (!result.ok) {
			setPagingError(result.error);
			return;
		}
		setOlder((previous) => [...previous, ...result.data.results]);
	};

	return (
		<section class="view" aria-labelledby="search-heading">
			<PageHeader
				id="search-heading"
				title="Search"
				about={{
					purpose:
						'One query across the conversation, the research, the evidence, the experiments and the governance record — with each result pointing at the record it came from.',
					how: (
						<p>
							Every word in the box must appear in a record, as a case-insensitive substring;
							partial words match, and the words may be in any order. Use the filters to narrow the
							families searched. A result opens the record in the view that owns it.
						</p>
					),
					limits: (
						<p>
							This search does not look at every store: it reads a bounded set of record families,
							and the coverage line above the results states how far back each one was walked and
							what was not searched. Nothing here is generative, and a search is not an action.
						</p>
					),
				}}
			/>

			<section class="panel" aria-labelledby="search-form-heading">
				<h2 id="search-form-heading" class="subhead">
					Find a record
				</h2>
				<form
					class="inline-form"
					onSubmit={(event: Event) => {
						event.preventDefault();
						submit(draft);
					}}
				>
					<input
						type="text"
						class="composer-input"
						aria-label="Search your records"
						placeholder="retrieval evaluation · charter · zq…"
						value={draft}
						onInput={(event: Event) => {
							setDraft((event.target as HTMLInputElement).value);
						}}
					/>{' '}
					<button type="submit" class="button">
						Search
					</button>
				</form>
				<div class="filterbar" role="group" aria-label="Which records to search">
					{GROUPS.map((entry) => (
						<button
							key={entry.id}
							type="button"
							class={`button${group === entry.id ? ' is-active' : ''}`}
							aria-pressed={group === entry.id}
							onClick={() => {
								setGroup(entry.id);
							}}
						>
							{entry.label}
						</button>
					))}
				</div>
				{/* THE MATCHING RULE IS THE SERVER'S SENTENCE, RENDERED RATHER THAN
				    RESTATED. A view that described the matching itself would be a
				    second place for it to be wrong — and the first version of this
				    screen did exactly that, spelling out a rule the gateway already
				    returns with every answer. The bounds beside it are the ones the
				    read actually enforces. */}
				{data === null || data === undefined ? (
					<p class="muted small">
						Search reads the records Scout holds. It is not a web search, it does not call the
						model, and it starts nothing.
					</p>
				) : (
					<p class="muted small">
						{data.limits.match}. At most {data.limits.maxTerms} words, {data.limits.queryMaxChars}{' '}
						characters in all, {data.limits.termMinChars} or more per word. Nothing here calls the
						model and nothing here starts research.
					</p>
				)}
			</section>

			{params === null && (
				<section class="panel" aria-labelledby="search-idle-heading">
					<h2 id="search-idle-heading" class="subhead">
						Nothing is being searched yet
					</h2>
					<p class="notice">
						<strong>Type a word and press Search.</strong>{' '}
						<span class="muted">
							This read looks at the records Scout holds — it is not a web search and it does
							not ask a model anything.
						</span>
					</p>
				</section>
			)}

			{params !== null && (
				<section class="panel" aria-labelledby="search-results-heading">
					<h2 id="search-results-heading" class="subhead">
						Results
					</h2>

					{state.status === 'loading' && (
						<p class="notice" role="status">
							Reading your records for “{query}”…
						</p>
					)}

					{state.status === 'failed' && state.error !== null && (
						<Failure
							error={state.error}
							what="the search"
							onRetry={() => void results.load()}
						/>
					)}

					{state.status === 'ready' && data !== null && (
						<>
							{data.results.length === 0 ? (
								<p class="notice">
									<strong>No record carries every one of those words.</strong>{' '}
									<span class="muted">
										That is a statement about the part of the store this read walked, and the
										line below says how far that was. A shorter or more ordinary word will
										usually find more.
									</span>
								</p>
							) : (
								<ul class="records">
									{shown.map((result) => {
										const target = anchorRoute(result);
										return (
											<li key={`${result.kind}:${result.id}:${result.at}`} class="record">
												<div class="record-head">
													<code>{result.id}</code>
													<span class="tag">{result.label}</span>
													{result.state !== null && <span class="tag">{result.state}</span>}
												</div>
												<p class="record-summary">{result.excerpt}</p>
												<p class="muted small">
													recorded <time dateTime={result.at}>{result.at}</time>
												</p>
												<p>
													{target.label.length === 0 ? (
														<span class="muted small">
															This build has no screen for a {result.kind} record, so it is
															shown here and not linked.
														</span>
													) : (
														<a
															class="button ghost small"
															href={hrefFor(target, result)}
															onClick={(event: MouseEvent) => {
																if (
																	event.metaKey ||
																	event.ctrlKey ||
																	event.shiftKey ||
																	event.altKey ||
																	event.button !== 0
																) {
																	return;
																}
																event.preventDefault();
																navigate(routeFor(target, result));
															}}
														>
															{target.label}
														</a>
													)}{' '}
													{/* WHETHER THE LINK OPENS THE RECORD OR THE LIST THAT HOLDS IT.
													    Four kinds have no per-record read (see §6 of this slice's
													    checkpoint), and a link labelled "Open the decision" that
													    lands on a bounded list without saying so is the kind of
													    small lie this workspace is built to avoid. The note is
													    rendered from the anchor table, so it cannot drift from
													    where the link actually goes. */}
													{target.label.length > 0 && !target.exact && (
														<span class="muted small">(the list that holds it, newest first)</span>
													)}{' '}
													{result.ref === null ? (
														<span class="muted small">
															No reference kind carries this record, so it cannot be handed
															to the conversation as a citation — an invented one would
															resolve to nothing.
														</span>
													) : (
														<DiscussLink ref={result.ref} label="Discuss with Scout" />
													)}
												</p>
											</li>
										);
									})}
								</ul>
							)}

							{/* THE BOUND, IN THE SERVER'S OWN WORDS. Paraphrasing it here
							    would be a second place for it to soften. */}
							<p class="countline small" aria-live="polite">
								<strong>
									{shown.length} result{shown.length === 1 ? '' : 's'}
								</strong>{' '}
								· {data.coverage.statement}
							</p>

							{!data.workspace.mainChatResolved && (
								<p class="muted small">
									No conversation has been resolved as the Main Chat in this workspace yet, so
									turns open in the archive. The Main Chat is decided the first time you open
									it, and this read does not decide it for you.
								</p>
							)}

							{pagingError !== null && (
								<Failure error={pagingError} what="the next page" onRetry={() => void loadOlder()} />
							)}

							{data.coverage.nextBefore !== null && (
								<p>
									<button
										type="button"
										class="button"
										disabled={paging}
										onClick={() => void loadOlder()}
									>
										{paging ? 'Reading more…' : 'Load more results'}
									</button>
								</p>
							)}
							{data.coverage.nextBefore === null && shown.length > 0 && (
								<p class="muted small">
									{data.coverage.exhaustive
										? 'That is everything this read compared.'
										: 'No further page — but see the coverage line above for what was not compared.'}
								</p>
							)}
						</>
					)}
				</section>
			)}

			{/* WHAT THE SEARCH DOES NOT LOOK AT STAYS IN THE OPEN. It is not
			    reference material about the screen; it is the boundary of the
			    answer, and a reader who has to press a disclosure to learn that
			    four record families were never compared has been told less than
			    the result implies. */}
			<section class="panel" aria-labelledby="search-limits">
				<h2 id="search-limits">What this search does not look at</h2>
				{data === undefined || data === null ? (
					<p class="muted small">The list arrives with the first search of this session.</p>
				) : (
					<ul>
						{data.notSearched.map((entry) => (
							<li key={entry.what}>
								<strong>{entry.what}</strong> <span class="muted">{entry.why}</span>
							</li>
						))}
					</ul>
				)}
				<p class="muted small">
					Nothing here is generative, and a search is not an action. Words found in a record stay
					words; the only thing a result can do is open the record or hand its reference to the Main
					Chat.
				</p>
			</section>
		</section>
	);
}

/** The address the link advertises, so a middle-click or a new tab lands on the
 *  same place the click handler would have gone. */
function hrefFor(target: AnchorRoute, result: SearchResult): string {
	const route = routeFor(target, result);
	const segments = [route.destination];
	if (route.section !== null) segments.push(route.section);
	if (route.recordId !== null) segments.push(route.recordId);
	return `/${segments.map((segment) => encodeURIComponent(segment)).join('/')}`;
}

/** What the record's own id in the path means depends on the screen: the
 *  Evidence reader wants a REFERENCE, the archive wants a conversation id. */
function routeFor(
	target: AnchorRoute,
	result: SearchResult,
): { destination: string; section: string | null; recordId: string | null } {
	if (target.destination === 'discoveries' && target.section === 'evidence') {
		// THE EVIDENCE READER WANTS A REFERENCE; an adjudication's own id is the
		// reference it resolves.
		return {
			destination: 'discoveries',
			section: 'evidence',
			recordId: result.kind === 'adjudication' ? result.anchor.id : (result.ref ?? result.id),
		};
	}
	if (target.destination === 'history' && target.section === 'conversations') {
		return {
			destination: 'history',
			section: 'conversations',
			recordId: result.anchor.conversationId ?? null,
		};
	}
	if (result.kind === 'charter_version') {
		// THE CHARTER PANEL RENDERS EVERY SIGNED VERSION with its number and its
		// text, so the record IS on that screen — and there is no per-version
		// address to give.
		return { destination: 'workspace', section: 'charter', recordId: null };
	}
	if (target.destination === 'chat') {
		// THE MAIN CHAT IS ONE PLACE AND THE PATH SAYS NOTHING MORE.
		return { destination: 'chat', section: null, recordId: null };
	}
	if (target.destination === 'workspace' && target.section === 'improvements') {
		// The proposal opens by its self-improvement id; the ledger candidate id
		// is the other name the same row answers to.
		return {
			destination: 'workspace',
			section: 'improvements',
			recordId: result.ref ?? result.anchor.id,
		};
	}
	return { destination: target.destination, section: target.section, recordId: result.anchor.id };
}
